// pages/purchase-entry/ExtractionStep.tsx

import { useEffect, useRef } from 'react';
import { Loader2, FileSearch, Sparkles, Check, X } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useInvoiceEntryStore, InvoiceStatus } from '@/stores/invoiceEntryStore';

const API_URL = import.meta.env.VITE_API_URL || '';
const POLL_INTERVAL = 3000; // 3 seconds

export function ExtractionStep() {
  const {
    batchId,
    company,
    transactionType,
    totalCount,
    extractedCount,
    failedCount,
    duplicateCount,
    batchDropdownData,
    setExtractedCount,
    setFailedCount,
    setDuplicateCount,
    setInvoices,
    updateInvoice,
    setBatchDropdownData,
    setCurrentStep,
    setError,
  } = useInvoiceEntryStore();

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isPollingRef = useRef(false);
  const dropdownDataFetchedRef = useRef(false);

  useEffect(() => {
    if (!batchId || isPollingRef.current) {
      console.error('[ExtractionStep] No batchId found');
      setCurrentStep('upload');
      return;
    }

    isPollingRef.current = true;

    const pollBatchStatus = async () => {
      try {
        const token = localStorage.getItem('auth_token');

        // Poll batch summary
        const batchResponse = await fetch(`${API_URL}/api/invoice-entry/batches/${batchId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!batchResponse.ok) {
          throw new Error('Failed to fetch batch status');
        }

        const batchData = await batchResponse.json();

        // Update batch counts
        setExtractedCount(batchData.extracted_count || 0);
        setFailedCount(batchData.failed_count || 0);
        setDuplicateCount(batchData.duplicate_count || 0);

        // Poll invoice list
        const invoicesResponse = await fetch(`${API_URL}/api/invoice-entry/batches/${batchId}/invoices`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!invoicesResponse.ok) {
          throw new Error('Failed to fetch invoice list');
        }

        const invoicesData: InvoiceStatus[] = await invoicesResponse.json();

        // Update store with invoice statuses
        invoicesData.forEach((invoice) => {
          updateInvoice(invoice.invoice_id, {
            invoice_id: invoice.invoice_id,
            file_name: invoice.file_name,
            file_url: invoice.file_url,
            extraction_status: invoice.extraction_status as any,
            extraction_job_id: invoice.extraction_job_id,
            voucher_status: invoice.voucher_status as any,
            voucher_job_id: invoice.voucher_job_id,
            tool_call_params: invoice.tool_call_params,
            error_message: invoice.error_message,
            is_duplicate: invoice.is_duplicate,
          });
        });

        // Check completion: (extracted + failed + duplicates) >= total
        const completedCount = batchData.extracted_count + batchData.failed_count + batchData.duplicate_count;
        const hasExtracted = batchData.extracted_count > 0;

        // Navigate to review as soon as at least 1 invoice is extracted
        if (hasExtracted && completedCount < batchData.total_count) {
          // At least 1 extracted, but not all complete yet
          // Store ALL invoices (including processing ones) so user can see full status
          setInvoices(
            invoicesData.map((inv) => ({
              invoice_id: inv.invoice_id,
              file_name: inv.file_name,
              file_url: inv.file_url,
              extraction_status: inv.extraction_status as any,
              extraction_job_id: inv.extraction_job_id,
              voucher_status: inv.voucher_status as any,
              voucher_job_id: inv.voucher_job_id,
              voucher_number: null,
              tool_call_params: inv.tool_call_params,
              error_message: inv.error_message,
              is_duplicate: inv.is_duplicate,
              // Note: Dropdown data now loaded globally via /batch-dropdown-data
            }))
          );

          // Navigate to batch-preview (user can review while others process)
          setCurrentStep('batch-preview');

          // Stop polling - batch-preview will poll for remaining invoices
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
          }
          isPollingRef.current = false;
          return;
        }

        if (completedCount >= batchData.total_count) {
          // All invoices processed
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
          }
          isPollingRef.current = false;

          // If ALL failed, show error and return to upload
          if (batchData.failed_count === batchData.total_count) {
            setError('All invoices failed extraction');
            setCurrentStep('upload');
            return;
          }

          // Store all invoice data before navigating
          setInvoices(
            invoicesData.map((inv) => ({
              invoice_id: inv.invoice_id,
              file_name: inv.file_name,
              file_url: inv.file_url,
              extraction_status: inv.extraction_status as any,
              extraction_job_id: inv.extraction_job_id,
              voucher_status: inv.voucher_status as any,
              voucher_job_id: inv.voucher_job_id,
              voucher_number: null,
              tool_call_params: inv.tool_call_params,
              error_message: inv.error_message,
              is_duplicate: inv.is_duplicate,
              // Note: Dropdown data now loaded globally via /batch-dropdown-data
            }))
          );

          // Navigate to batch-preview
          setCurrentStep('batch-preview');
          return;
        }
      } catch (err) {
        console.error('Polling error:', err);
        // Continue polling despite errors
      }
    };

    // Initial poll
    pollBatchStatus();

    // Poll every 3 seconds
    pollingRef.current = setInterval(pollBatchStatus, POLL_INTERVAL);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      isPollingRef.current = false;
    };
  }, [batchId]);

  // Fetch batch dropdown data once when first invoice completes
  useEffect(() => {
    const fetchBatchDropdownData = async () => {
      if (!company || !transactionType || dropdownDataFetchedRef.current || batchDropdownData) {
        return;
      }

      // Wait for at least one invoice to complete
      if (extractedCount === 0) {
        return;
      }

      dropdownDataFetchedRef.current = true;

      try {
        console.log('[ExtractionStep] Fetching batch dropdown data...');
        const token = localStorage.getItem('auth_token');
        const response = await fetch(
          `${API_URL}/api/invoice-entry/batch-dropdown-data?company=${encodeURIComponent(company)}&transaction_type=${transactionType}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch batch dropdown data');
        }

        const data = await response.json();
        console.log('[ExtractionStep] Batch dropdown data fetched:', {
          ledgers: data.ledgers?.length,
          stockItems: data.stock_items?.length,
          godowns: data.godowns?.length,
          gstLedgers: data.gst_ledgers?.length,
          expenseLedgers: data.expense_ledgers?.length,
          transactionAccounts: data.transaction_accounts?.length,
          voucherTypes: data.voucher_types?.length,
        });

        setBatchDropdownData({
          ledgers: data.ledgers || [],
          stockItems: data.stock_items || [],
          godowns: data.godowns || [],
          gstLedgers: data.gst_ledgers || [],
          expenseLedgers: data.expense_ledgers || [],
          transactionAccounts: data.transaction_accounts || [],
          voucherTypes: data.voucher_types || [],
        });
      } catch (err) {
        console.error('[ExtractionStep] Failed to fetch batch dropdown data:', err);
        // Don't block the UI - user can still proceed, but dropdowns might be empty
      }
    };

    fetchBatchDropdownData();
  }, [extractedCount, company, transactionType, batchDropdownData, setBatchDropdownData]);

  // Calculate overall progress
  const overallProgress = totalCount > 0 ? ((extractedCount + failedCount + duplicateCount) / totalCount) * 100 : 0;

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'pending':
        return (
          <Badge variant="secondary" className="bg-gray-200 text-gray-700">
            Pending
          </Badge>
        );
      case 'processing':
        return (
          <Badge variant="default" className="bg-blue-500 text-white flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            Processing
          </Badge>
        );
      case 'completed':
        return (
          <Badge variant="default" className="bg-green-500 text-white flex items-center gap-1">
            <Check className="h-3 w-3" />
            Completed
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="destructive" className="flex items-center gap-1">
            <X className="h-3 w-3" />
            Failed
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" className="bg-gray-200 text-gray-700">
            Unknown
          </Badge>
        );
    }
  };

  return (
    <div className="space-y-8 py-8 max-w-4xl mx-auto">
      {/* Overall Progress */}
      <div className="flex flex-col items-center space-y-6">
        <div className="relative">
          <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center">
            <FileSearch className="h-6 w-6 text-blue-500" />
          </div>
          <div className="absolute -bottom-1 -right-1">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        </div>

        <div className="text-center space-y-2">
          <h3 className="text-lg font-medium">Extracting Invoices...</h3>
          <p className="text-sm text-muted-foreground">
            {extractedCount + failedCount + duplicateCount}/{totalCount} complete
          </p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="space-y-2 max-w-md mx-auto">
        <Progress value={overallProgress} className="h-2" />
        <p className="text-xs text-center text-muted-foreground">
          {Math.round(overallProgress)}% complete
        </p>
      </div>

      {/* Counts */}
      <div className="flex justify-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <Check className="h-4 w-4 text-green-600" />
          <span>Extracted: {extractedCount}</span>
        </div>
        <div className="flex items-center gap-2">
          <X className="h-4 w-4 text-red-600" />
          <span>Failed: {failedCount}</span>
        </div>
        {duplicateCount > 0 && (
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-blue-500" />
            <span>Duplicates: {duplicateCount}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-gray-500" />
          <span>Pending: {totalCount - extractedCount - failedCount - duplicateCount}</span>
        </div>
      </div>

      {/* Invoice List - placeholder for now, will be populated by actual invoice data */}
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-center">Invoice Status</h4>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {/* This will be populated when we have actual invoice data from the store */}
          <p className="text-xs text-center text-muted-foreground">
            Processing {totalCount} invoices...
          </p>
        </div>
      </div>
    </div>
  );
}
